import { useEffect, useState } from 'react';
import colors from '@/theme/colors';
import { GoogleMap, Marker, Circle } from '@react-google-maps/api';
import schoolFinderPin from '@/assets/school-finder-pin.png';
import schoolInYourDistrictIcon from '@/assets/school-in-your-district.svg';
import schoolOutsideIcon from '@/assets/school-outside-your-district.svg';

import styles from '../Community.styles';
import {
  DEFAULT_MAP_STYLES,
  DEFAULT_MIN_ZOOM,
  DEFAULT_MAX_ZOOM,
  DEFAULT_MAX_LATITUDE,
  DEFAULT_MIN_LATITUDE,
  DEFAULT_MAX_LONGITUDE,
  DEFAULT_MIN_LONGITUDE,
} from './Map.constantes';
import { Location, SchoolResult } from '@/graphql/types';
import { SchoolsList } from './SchoolsList';

interface MapProps {
  distance: number;
  metricType: string;
  latitude: number;
  longitude: number;
  updateMapPosition: (location: Location) => void;
  districtSchools: SchoolResult[];
}

export const Map = ({
  distance,
  metricType,
  latitude,
  longitude,
  updateMapPosition,
  districtSchools,
}: MapProps) => {
  const mapPosition = {
    lat: latitude,
    lng: longitude,
  };
  const [map, setMap] = useState();
  const [mapZoom, setMapZoom] = useState(8);
  const distanceInMeters = metricType === 'mi' ? distance * 1609.34 : distance * 1000;

  const mapOptions = {
    styles: DEFAULT_MAP_STYLES,
    mapTypeControl: false,
    streetViewControl: false,
    scrollwheel: true,
    scaleControl: true,
    keyboardShortcuts: false,
    fullscreenControl: false,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    restriction: {
      latLngBounds: {
        north: DEFAULT_MAX_LATITUDE,
        south: DEFAULT_MIN_LATITUDE,
        west: DEFAULT_MIN_LONGITUDE,
        east: DEFAULT_MAX_LONGITUDE,
      },
      strictBounds: true,
    },
  };

  const SCHOOL_PIN_ICON = {
    url: schoolFinderPin,
    origin: null,
    anchor: new google.maps.Point(8, 25),
    scaledSize: new google.maps.Size(32, 32),
  };

  const circleOptions = {
    strokeColor: colors.leaf,
    strokeOpacity: 0.8,
    strokeWeight: 1,
    fillColor: colors.leaf,
    fillOpacity: 0.2,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    radius: distanceInMeters,
    zIndex: 1,
  };

  useEffect(() => {
    let newMapZoom;

    if (distance <= 6) {
      newMapZoom = 10.5;
    } else if (distance <= 8) {
      newMapZoom = 10.2;
    } else if (distance <= 10) {
      newMapZoom = 10;
    } else if (distance < 20) {
      newMapZoom = 8.8;
    } else if (distance < 30) {
      newMapZoom = 8.2;
    } else if (distance < 40) {
      newMapZoom = 7.8;
    } else if (distance < 50) {
      newMapZoom = 7.4;
    } else if (distance < 60) {
      newMapZoom = 7.3;
    } else if (distance <= 80) {
      newMapZoom = 7.4;
    }

    setMapZoom(newMapZoom);
  }, [distance]);

  const onDragEnd = async () => {
    // @ts-ignore
    const lat = map.getPosition().lat();
    // @ts-ignore
    const lng = map.getPosition().lng();
    const location: Location = {
      lat,
      lng,
    };
    updateMapPosition(location);
  };

  const centerLocation: Location = {
    lat: latitude,
    lng: longitude,
  };

  return (
    <>
      <div className={styles.map}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapPosition}
          zoom={mapZoom}
          options={mapOptions}
          id="googleMap"
        >
          <Marker
            // @ts-ignore
            position={mapPosition}
            icon={SCHOOL_PIN_ICON}
            draggable
            onLoad={map => {
              // @ts-ignore
              setMap(map);
            }}
            onDragEnd={onDragEnd}
            zIndex={1}
            data-testid="marker"
          >
            <Circle center={mapPosition} options={circleOptions} data-testid="circle" />
          </Marker>
          {districtSchools.map(({ location, isPin }, index) => (
            <Marker
              key={index}
              position={location}
              icon={{
                url: isPin ? schoolInYourDistrictIcon : schoolOutsideIcon,
                scaledSize: new window.google.maps.Size(30, 30),
              }}
              zIndex={0}
            />
          ))}
        </GoogleMap>
      </div>
      <SchoolsList
        schools={districtSchools}
        centerLocation={centerLocation}
        metricType={metricType}
      />
    </>
  );
};
